import { Typography } from '@euler/app/components/typography';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import {
  ApdmCampaignStatus,
  ApdmCampaignVersionRef,
  ApdmCouponCampaignSummary,
  ApdmCouponChoicesResult,
} from '@euler/model/apdm';
import { parseApdmCouponWidgetPayload } from '@euler/model/apdm/widgets';
import { useServiceFactory } from '@euler/services/factory';
import { CirclePlus, Ticket } from 'lucide-react-native';
import { memo, useCallback, useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useAiChatActions, useAiChatMessage } from '../chat/AiChatActions';
import {
  CardLoading,
  CardNotice,
  ChatCard,
  ChatCardBadge,
} from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import {
  couponItemText,
  couponPeriodText,
  couponStockText,
} from '../components/couponText';
import { aiColors, tint } from '../theme';
import { CouponCampaignDetailSheet } from './CouponCampaignDetailSheet';

const kPageSize = 10;
const kCollapsedCount = 3;
const kItemsShown = 3;

/**
 * Which coupon campaign the offer carries. A pick is saved on the draft
 * here, then said in the conversation so the assistant moves on with it.
 */
export const CouponChoiceCard = memo(
  ({ data, streaming }: { data: unknown; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    const ref = parseApdmCouponWidgetPayload(data);
    if (streaming) return null;
    if (!ref)
      return <CardNotice text="这张卡片的引用无效，请让助手重新列出。" />;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法为活动选择优惠券。" />;
    return <CouponChoiceBody key={ref.campaignId} campaign={ref} />;
  },
);

function CouponChoiceBody({ campaign }: { campaign: ApdmCampaignVersionRef }) {
  const { rpx } = useResponsiveContext();
  const { apdmCampaignService } = useServiceFactory();
  const chat = useAiChatActions();
  const { latest } = useAiChatMessage();
  const [result, setResult] = useState<ApdmCouponChoicesResult | null>(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState<'use' | 'none' | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [unsent, setUnsent] = useState<{
    choice: string;
    message: string;
  } | null>(null);
  const [detail, setDetail] = useState<ApdmCouponCampaignSummary | null>(null);

  const load = useCallback(
    async (after: string | null) => {
      const page = await apdmCampaignService.couponChoices({
        campaignId: campaign.campaignId,
        afterCouponCampaignId: after,
        limit: kPageSize,
      });
      setResult(previous =>
        previous && after
          ? { ...page, coupons: [...previous.coupons, ...page.coupons] }
          : page,
      );
      if (!after)
        setSelected(
          current =>
            current ??
            page.selectedCouponCampaignId ??
            page.coupons[0]?.id ??
            null,
        );
    },
    [apdmCampaignService, campaign.campaignId],
  );
  useEffect(() => {
    load(null).catch(error => {
      console.error('APDM coupon choices failed', error);
      setFailed(true);
    });
  }, [load]);

  // The draft moves on as the assistant edits it, so save on its current
  // version rather than the one this card was drawn for.
  const save = async (
    mode: 'use' | 'none',
    couponCampaignId: string | null,
    message: string,
  ) => {
    if (chat.busy) return;
    setSaving(mode);
    setSaveError(null);
    try {
      const current = await apdmCampaignService.detail({
        campaignId: campaign.campaignId,
      });
      // A confirmed campaign would silently go back to draft: refuse.
      if (current.summary.status !== ApdmCampaignStatus.Draft) {
        setSaveError('活动已确认，请在活动详情中修改优惠券。');
        return;
      }
      await apdmCampaignService.setCoupon({
        campaignId: campaign.campaignId,
        expectedVersion: current.summary.version,
        couponCampaignId,
      });
      tell(couponCampaignId ?? 'none', message);
    } catch (error) {
      console.error('APDM coupon choice failed', error);
      setSaveError('没有保存成功，请稍后重试。');
    } finally {
      setSaving(null);
    }
  };

  // The choice is saved; the assistant hears of it as a message. If a reply
  // started meanwhile, the message waits for the user to send it on.
  function tell(choice: string, message: string) {
    if (chat.send(message)) {
      setUnsent(null);
      setChosen(choice);
    } else setUnsent({ choice, message });
  }

  if (failed)
    return (
      <ChatCard icon={Ticket} title="选择优惠券活动">
        <CardNotice tone="danger" text="优惠券活动没有加载出来，请稍后重试。" />
      </ChatCard>
    );
  if (!result)
    return (
      <ChatCard icon={Ticket} title="选择优惠券活动">
        <CardLoading label="正在读取门店的优惠券活动…" />
      </ChatCard>
    );

  const coupons = result.coupons;
  const shown = expanded ? coupons : coupons.slice(0, kCollapsedCount);
  const picked = coupons.find(coupon => coupon.id === selected) ?? null;
  const locked = chat.busy || saving != null;
  const createNew = () => {
    if (!locked && chat.send('为这次活动新建一个优惠券活动')) setChosen('new');
  };

  // An older reply's picker was answered one way or another: report what
  // the draft carries now instead of asking again.
  const settled =
    chosen ?? (latest ? null : result.selectedCouponCampaignId ?? 'unset');
  if (settled)
    return (
      <ChatCard
        icon={Ticket}
        title={
          settled === 'none'
            ? '这次不带优惠券'
            : settled === 'unset'
            ? '活动目前没有优惠券活动'
            : settled === 'new'
            ? '新建优惠券活动'
            : `已选择「${
                coupons.find(coupon => coupon.id === settled)?.title ??
                '优惠券活动'
              }」`
        }
        badge={<ChatCardBadge label="已确认" color={aiColors.green} />}
      />
    );

  if (!coupons.length)
    return (
      <ChatCard
        icon={Ticket}
        title="还没有可用的优惠券活动"
        subtitle="这个优惠需要一个优惠券活动。新建一个，每位客户都会收到自己的核销二维码。"
      >
        <PrimaryButton
          title="新建优惠券活动"
          icon={CirclePlus}
          disabled={locked}
          onPress={createNew}
        />
        <NoCouponLink
          disabled={locked}
          onPress={() => void save('none', null, '这次不带优惠券，直接发送')}
        />
      </ChatCard>
    );

  return (
    <ChatCard
      icon={Ticket}
      title="选择优惠券活动"
      subtitle="这个优惠需要一个优惠券活动。选一个现有的，或新建一个。"
    >
      <View style={{ gap: rpx(10) }}>
        {shown.map(coupon => (
          <CouponOption
            key={coupon.id}
            coupon={coupon}
            selected={coupon.id === selected}
            onSelect={() => setSelected(coupon.id)}
            onDetail={() => setDetail(coupon)}
          />
        ))}
      </View>
      {(coupons.length > kCollapsedCount ||
        result.nextAfterCouponCampaignId) && (
        <Pressable
          accessibilityRole="button"
          onPress={() => {
            if (!expanded) setExpanded(true);
            else if (result.nextAfterCouponCampaignId && !loadingMore) {
              setLoadingMore(true);
              load(result.nextAfterCouponCampaignId)
                .catch(error =>
                  console.error('APDM coupon choices page failed', error),
                )
                .finally(() => setLoadingMore(false));
            }
          }}
          style={{ alignItems: 'center', paddingVertical: rpx(4) }}
        >
          <Typography.Text medium size={rpx(13)} color={aiColors.primary}>
            {loadingMore
              ? '正在加载…'
              : !expanded
              ? `显示全部${
                  result.nextAfterCouponCampaignId
                    ? ''
                    : ` ${coupons.length} 个`
                }`
              : result.nextAfterCouponCampaignId
              ? '加载更多'
              : ''}
          </Typography.Text>
        </Pressable>
      )}
      {saveError && <CardNotice tone="danger" text={saveError} />}
      {unsent && (
        <>
          <CardNotice text="选择已保存。等当前回复结束后，点继续告诉助手。" />
          <PrimaryButton
            title="继续"
            disabled={chat.busy}
            onPress={() => tell(unsent.choice, unsent.message)}
          />
        </>
      )}
      <PrimaryButton
        title="使用这个优惠券活动"
        loading={saving === 'use'}
        disabled={!picked || locked}
        onPress={() =>
          picked &&
          void save('use', picked.id, `使用优惠券活动「${picked.title}」`)
        }
      />
      <View style={{ flexDirection: 'row' }}>
        <SecondaryButton
          title="新建优惠券活动"
          icon={CirclePlus}
          disabled={locked}
          onPress={createNew}
        />
      </View>
      <NoCouponLink
        disabled={locked}
        loading={saving === 'none'}
        onPress={() => void save('none', null, '这次不带优惠券，直接发送')}
      />
      <CouponCampaignDetailSheet
        coupon={detail}
        onClose={() => setDetail(null)}
      />
    </ChatCard>
  );
}

const CouponOption = memo(
  ({
    coupon,
    selected,
    onSelect,
    onDetail,
  }: {
    coupon: ApdmCouponCampaignSummary;
    selected: boolean;
    onSelect: () => void;
    onDetail: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const more = coupon.items.length - kItemsShown;
    return (
      <Pressable
        accessibilityRole="radio"
        accessibilityState={{ checked: selected }}
        onPress={onSelect}
        style={{
          flexDirection: 'row',
          gap: rpx(10),
          padding: rpx(12),
          borderRadius: rpx(12),
          borderWidth: selected ? 2 : 1,
          borderColor: selected ? aiColors.primary : aiColors.border,
          backgroundColor: selected
            ? tint(aiColors.primary, 0.05)
            : aiColors.card,
        }}
      >
        <View
          style={{
            width: rpx(18),
            height: rpx(18),
            borderRadius: rpx(9),
            borderWidth: 2,
            borderColor: selected ? aiColors.primary : aiColors.border,
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: rpx(2),
          }}
        >
          {selected && (
            <View
              style={{
                width: rpx(8),
                height: rpx(8),
                borderRadius: rpx(4),
                backgroundColor: aiColors.primary,
              }}
            />
          )}
        </View>
        <View style={{ flex: 1, gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: rpx(8),
            }}
          >
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              style={{ flexShrink: 1 }}
            >
              {coupon.title}
            </Typography.Text>
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              {coupon.items.length} 项
            </Typography.Text>
          </View>
          {coupon.description ? (
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              numberOfLines={2}
            >
              {coupon.description}
            </Typography.Text>
          ) : null}
          <View style={{ gap: rpx(2), marginTop: rpx(2) }}>
            {coupon.items.slice(0, kItemsShown).map((item, index) => (
              <Typography.Text
                key={index}
                size={rpx(12)}
                color={aiColors.foreground}
              >
                · {couponItemText(item)}
              </Typography.Text>
            ))}
            {more > 0 && (
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                还有 {more} 项
              </Typography.Text>
            )}
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginTop: rpx(4),
            }}
          >
            <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
              {couponPeriodText(coupon)}
              {'\n'}
              {couponStockText(coupon)}
            </Typography.Text>
            <Pressable accessibilityRole="link" onPress={onDetail} hitSlop={8}>
              <Typography.Text medium size={rpx(12)} color={aiColors.primary}>
                详情
              </Typography.Text>
            </Pressable>
          </View>
        </View>
      </Pressable>
    );
  },
);

const NoCouponLink = memo(
  ({
    disabled,
    loading,
    onPress,
  }: {
    disabled: boolean;
    loading?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <Pressable
        accessibilityRole="button"
        disabled={disabled}
        onPress={onPress}
        style={{ alignItems: 'center', opacity: disabled ? 0.5 : 1 }}
      >
        <Typography.Text
          size={rpx(12)}
          color={aiColors.mutedForeground}
          decorationLine="underline"
        >
          {loading ? '正在保存…' : '不带优惠券，直接发送'}
        </Typography.Text>
      </Pressable>
    );
  },
);
